import { MoreHorizontal, Pencil, FileText, CheckCircle, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import type { JournalEntrySummary, JournalEntryStatus } from "../types";

interface JournalEntryTableProps {
  entries: JournalEntrySummary[];
  onEdit: (entry: JournalEntrySummary) => void;
  onView: (entry: JournalEntrySummary) => void;
}

/**
 * Status badge colors and labels.
 */
const statusConfig: Record<
  JournalEntryStatus,
  { label: string; className: string; icon: typeof CheckCircle }
> = {
  DRAFT: {
    label: "Borrador",
    className: "bg-yellow-100 text-yellow-800",
    icon: FileText,
  },
  POSTED: {
    label: "Contabilizado",
    className: "bg-green-100 text-green-800",
    icon: CheckCircle,
  },
  REVERSED: {
    label: "Anulado",
    className: "bg-red-100 text-red-800",
    icon: XCircle,
  },
};

/**
 * Format a number as currency.
 */
function formatAmount(amount: string): string {
  const num = parseFloat(amount);
  return new Intl.NumberFormat("es-CL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

/**
 * Format a date string.
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString + "T00:00:00");
  return date.toLocaleDateString("es-CL", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
}

/**
 * Table component for displaying journal entries.
 */
export function JournalEntryTable({
  entries,
  onEdit,
  onView,
}: JournalEntryTableProps) {
  if (entries.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No hay asientos contables
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b text-left text-sm text-muted-foreground">
            <th className="pb-2 font-medium">Fecha</th>
            <th className="pb-2 font-medium">Nro</th>
            <th className="pb-2 font-medium">Descripcion</th>
            <th className="pb-2 font-medium text-right">Debitos</th>
            <th className="pb-2 font-medium text-right">Creditos</th>
            <th className="pb-2 font-medium">Estado</th>
            <th className="pb-2 font-medium w-10"></th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => {
            const status = statusConfig[entry.status];
            const StatusIcon = status.icon;
            const canEdit = entry.status === "DRAFT";

            return (
              <tr
                key={entry.id}
                className="group border-b last:border-0 hover:bg-muted/50 cursor-pointer transition-colors"
                onClick={() => onView(entry)}
              >
                <td className="py-3 text-sm">
                  {formatDate(entry.transactionDate)}
                </td>
                <td className="py-3 font-mono text-sm text-muted-foreground">
                  {String(entry.entryNumber).padStart(6, "0")}
                </td>
                <td className="py-3 text-sm max-w-xs truncate">
                  {entry.description}
                  {entry.reference && (
                    <span className="text-muted-foreground ml-2">
                      ({entry.reference})
                    </span>
                  )}
                </td>
                <td className="py-3 text-sm text-right font-mono">
                  {formatAmount(entry.totalDebit)}
                </td>
                <td className="py-3 text-sm text-right font-mono">
                  {formatAmount(entry.totalCredit)}
                </td>
                <td className="py-3">
                  <span
                    className={cn(
                      "inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full",
                      status.className
                    )}
                  >
                    <StatusIcon className="h-3 w-3" />
                    {status.label}
                  </span>
                </td>
                <td className="py-3" onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                        <span className="sr-only">Acciones</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {canEdit ? (
                        <DropdownMenuItem onClick={() => onEdit(entry)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          Editar
                        </DropdownMenuItem>
                      ) : (
                        <div className="px-2 py-1.5 text-sm text-muted-foreground">
                          Sin acciones disponibles
                        </div>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
